/**
 * Format PIR data into a description string for the source package summary API
 * @param {Object} icr - The PIR object
 * @returns {string} Formatted PIR description
 */
export function formatIcrDescription(icr) {
    if (!icr) return '';

    const sections = [];
    const question = icr.question || icr.informationRequirement || icr.subject || '';

    sections.push('1. Intelligence Question\n');
    if (question) {
        sections.push(`${question}\n`);
    }

    sections.push('\n2. Purpose / Justification\n');
    if (icr.backgroundJustification) {
        sections.push(`${icr.backgroundJustification}\n`);
    }

    sections.push('\n3. Essential Elements of Information (EEIs)\n');

    let eeiLines = [];
    if (Array.isArray(icr.eeis) && icr.eeis.length > 0) {
        eeiLines = icr.eeis.map((e, i) => `${i + 1}. ${typeof e === 'string' ? e : e?.text || ''}`).filter((l) => !l.endsWith('. '));
    } else {
        const eei = icr.eei || {};
        if (eei.what) eeiLines.push(`What: ${eei.what}`);
        if (eei.where) eeiLines.push(`Where: ${eei.where}`);
        if (eei.when) eeiLines.push(`When: ${eei.when}`);
        if (eei.detail) eeiLines.push(`Detail: ${eei.detail}`);
    }

    if (eeiLines.length > 0) {
        sections.push(`\n${eeiLines.join('\n\n')}\n`);
    }

    const targetInfo = icr.targetInformation || {};
    if (targetInfo.targetNameTypeActivity || targetInfo.geographicArea || targetInfo.timeOfInterest) {
        sections.push('\nTarget Information:\n');
        if (targetInfo.targetNameTypeActivity) {
            sections.push(`- Target/Activity: ${targetInfo.targetNameTypeActivity}\n`);
        }
        if (targetInfo.geographicArea) {
            sections.push(`- Geographic Area: ${targetInfo.geographicArea}\n`);
        }
        if (targetInfo.timeOfInterest) {
            sections.push(`- Time of Interest: ${targetInfo.timeOfInterest}\n`);
        }
    }

    return sections.join('');
}

/**
 * Convert frontend PIR shape to backend structured payload expected by summary endpoints.
 */
export function toBackendIcr(icr) {
    if (!icr) return null;

    const targetInfo = icr.targetInformation || {};
    const question = icr.question || icr.informationRequirement || icr.subject || '';

    let eeiText = '';
    if (Array.isArray(icr.eeis) && icr.eeis.length > 0) {
        eeiText = icr.eeis.map((e) => (typeof e === 'string' ? e : e?.text || '')).filter(Boolean).join('\n');
    } else {
        const eei = icr.eei || {};
        const eeiParts = [];
        if (eei.what) eeiParts.push(`What: ${eei.what}`);
        if (eei.where) eeiParts.push(`Where: ${eei.where}`);
        if (eei.when) eeiParts.push(`When: ${eei.when}`);
        if (eei.detail) eeiParts.push(`Detail: ${eei.detail}`);
        eeiText = eeiParts.join('\n');
    }

    return {
        informationRequirement: question,
        question,
        eeis: Array.isArray(icr.eeis) ? icr.eeis : undefined,
        eei: eeiText,
        backgroundJustification: icr.backgroundJustification || '',
        targetInformation: {
            nameTypeActivity: targetInfo.targetNameTypeActivity || targetInfo.nameTypeActivity || '',
            geographicArea: targetInfo.geographicArea || '',
        },
        constraintsSpecialInstructions: icr.constraintsSpecialInstructions || '',
    };
}
